import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { FileArchive, FlaskConical, GitBranch, Link2, MoreHorizontal, Undo2 } from "lucide-react";
import { useApi, useAuth, type WorkflowInstanceView } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger, Field, Input, NativeSelect, Popover, PopoverContent, PopoverTrigger, StatusBadge, Textarea, Tooltip, useSave, toast } from "@ncp/ui";
import { useHeadStandsIn } from "@/lib/decisionControls";

interface UndoView { available: boolean; reason?: string | null; reasonCode?: string | null; fromState?: string | null; fromLabel?: string | null; fromLabelAr?: string | null; toLabel?: string | null; toLabelAr?: string | null; firedAt?: string | null; firedBy?: string | null }
interface UndoResult { succeeded: boolean; error?: string | null; state?: string | null }
interface LinkedCase { id: string; caseNumber: string; type: string; title: string; titleAr?: string | null; status: string; relation: "Parent" | "Referral" | "Related" }
interface ReferralTarget { type: string; section: string }

const CASE_KEYS = (caseId: string) => [["case", caseId], ["case-workflow", caseId], ["case-review", caseId], ["case-review-history", caseId], ["case-events", caseId], ["case-tasks", caseId], ["case-undo", caseId], ["case-links", caseId], ["cases"], ["my-work"]];

/** Reverses the last step taken on a case, where the platform still allows it. */
export function useUndoStep(caseId: string) {
  const api = useApi();
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useSave({
    mutationFn: (note?: string) => api.post<UndoResult>(`/api/cases/${caseId}/workflow/undo`, { note: note ?? null }),
    saved: t("backoffice:workspace.undo.done"),
    failed: t("backoffice:workspace.undo.failed"),
    onSuccess: () => {
      for (const k of CASE_KEYS(caseId)) void qc.invalidateQueries({ queryKey: k });
    },
  });
}

/**
 * Less frequent actions on the case, out of the way: undo the last step, refer to another section, mark the file as a test, export the
 * file's administrative record for a grievance or an appeal.
 */
export function CaseActions({ caseId, caseNumber, workflow, isTest }: { caseId: string; caseNumber?: string; workflow?: WorkflowInstanceView; isTest?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const headStandsIn = useHeadStandsIn();
  const canMarkTest = !isTest && ((user?.roles ?? []).some((r) => ["Director", "Admin"].includes(r)) || headStandsIn);
  const today = new Date().toISOString().slice(0, 10);
  const [testing, setTesting] = useState(false);
  const [asOf, setAsOf] = useState("");
  const [testReason, setTestReason] = useState("");
  const markTest = useSave({
    mutationFn: () => api.post<{ isTest: boolean; asOf: string }>(`/api/cases/${caseId}/test-mode`, { asOf, reason: testReason }),
    saved: t("backoffice:workspace.testMode.done"),
    onSuccess: () => {
      setTesting(false); setTestReason("");
      for (const k of CASE_KEYS(caseId)) void qc.invalidateQueries({ queryKey: k });
    },
  });
  const undoView = useQuery({ queryKey: ["case-undo", caseId, workflow?.currentState], queryFn: () => api.get<UndoView>(`/api/cases/${caseId}/workflow/undo`) });
  const targets = useQuery({ queryKey: ["referral-targets", caseId], queryFn: () => api.get<ReferralTarget[]>(`/api/cases/${caseId}/referral-targets`) });
  const undo = useUndoStep(caseId);
  const [confirmUndo, setConfirmUndo] = useState(false);
  const [note, setNote] = useState("");
  const [referring, setReferring] = useState(false);
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const refer = useSave({
    mutationFn: () => api.post<{ caseId: string; caseNumber: string }>(`/api/cases/${caseId}/refer`, { targetType: target, reason }),
    saved: (r) => t("backoffice:workspace.refer.done", { caseNumber: r.caseNumber }),
    onSuccess: (r) => {
      setReferring(false); setReason("");
      for (const k of CASE_KEYS(caseId)) void qc.invalidateQueries({ queryKey: k });
      navigate(`/cases/${r.caseId}`);
    },
  });
  // The whole record as one ZIP (condition 2.18); the server checks access, writes the export into the audit trail and names the file.
  const exportRecord = useSave({
    mutationFn: () => api.download(`/api/cases/${caseId}/administrative-record`, caseNumber ? `${caseNumber}-administrative-record.zip` : undefined),
    onMutate: () => { toast.info(t("backoffice:workspace.administrativeRecord.preparing")); },
    saved: { title: t("backoffice:workspace.administrativeRecord.done", { caseNumber: caseNumber ?? "" }), description: t("backoffice:workspace.administrativeRecord.doneHint") },
    failed: t("backoffice:workspace.administrativeRecord.failed"),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["case-audit", caseId] });
    },
  });
  const u = undoView.data;
  const ar = locale === "ar";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild><Button variant="outline" size="icon" aria-label={t("backoffice:workspace.moreActions")}><MoreHorizontal /></Button></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>{t("backoffice:workspace.moreActions")}</DropdownMenuLabel>
          <DropdownMenuItem disabled={!u?.available} onSelect={() => setConfirmUndo(true)}>
            <Undo2 />
            <span className="min-w-0 flex-1">
              <span className="block">{t("backoffice:workspace.undo.action")}</span>
              <span className="block truncate text-[11px] text-muted-foreground">{u?.available ? `${ar ? u.toLabelAr : u.toLabel} → ${ar ? u.fromLabelAr : u.fromLabel}` : u?.reasonCode ? t(`backoffice:workspace.undo.reasons.${u.reasonCode}`, { defaultValue: u.reason ?? "" }) : t("backoffice:workspace.undo.nothing")}</span>
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem disabled={!targets.data?.length} onSelect={() => { setTarget(targets.data?.[0]?.type ?? ""); setReferring(true); }}>
            <GitBranch />
            <span className="min-w-0 flex-1">
              <span className="block">{t("backoffice:workspace.refer.action")}</span>
              <span className="block truncate text-[11px] text-muted-foreground">{t("backoffice:workspace.refer.hint")}</span>
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem disabled={exportRecord.isPending} onSelect={() => exportRecord.mutate()}>
            <FileArchive />
            <span className="min-w-0 flex-1">
              <span className="block">{t("backoffice:workspace.administrativeRecord.action")}</span>
              <span className="block truncate text-[11px] text-muted-foreground">{exportRecord.isPending ? t("backoffice:workspace.administrativeRecord.preparing") : t("backoffice:workspace.administrativeRecord.hint")}</span>
            </span>
          </DropdownMenuItem>
          {canMarkTest ? (
            <DropdownMenuItem onSelect={() => { setAsOf(""); setTesting(true); }}>
              <FlaskConical />
              <span className="min-w-0 flex-1">
                <span className="block">{t("backoffice:workspace.testMode.action")}</span>
                <span className="block truncate text-[11px] text-muted-foreground">{t("backoffice:workspace.testMode.hint")}</span>
              </span>
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={testing} onOpenChange={setTesting}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{t("backoffice:workspace.testMode.title")}</DialogTitle>
            <DialogDescription>{t("backoffice:workspace.testMode.body")}</DialogDescription>
          </DialogHeader>
          <Field label={t("backoffice:workspace.testMode.asOf")} hint={t("backoffice:workspace.testMode.asOfHint")}>{(id) => <Input id={id} type="date" dir="ltr" max={today} value={asOf} onChange={(e) => setAsOf(e.target.value)} />}</Field>
          <Field label={t("backoffice:workspace.testMode.reason")}>{(id) => <Textarea id={id} rows={4} value={testReason} onChange={(e) => setTestReason(e.target.value)} draft={`case:${caseId}:test-mode:reason`} />}</Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTesting(false)}>{t("actions.cancel")}</Button>
            <Button variant="gold" loading={markTest.isPending} disabled={!asOf || asOf > today || testReason.trim().length < 20} onClick={() => markTest.mutate()}><FlaskConical /> {t("backoffice:workspace.testMode.submit")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmUndo} onOpenChange={setConfirmUndo}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{t("backoffice:workspace.undo.confirmTitle")}</DialogTitle>
            <DialogDescription>{u ? t("backoffice:workspace.undo.confirmBody", { from: ar ? u.toLabelAr : u.toLabel, to: ar ? u.fromLabelAr : u.fromLabel }) : null}</DialogDescription>
          </DialogHeader>
          <Field label={t("backoffice:workspace.undo.note")}>{(id) => <Textarea id={id} rows={3} value={note} onChange={(e) => setNote(e.target.value)} draft={`case:${caseId}:undo:${workflow?.currentState ?? "step"}:note`} />}</Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmUndo(false)}>{t("actions.cancel")}</Button>
            <Button variant="gold" loading={undo.isPending} disabled={note.trim().length < 5} onClick={() => undo.mutate(note, { onSuccess: () => { setConfirmUndo(false); setNote(""); } })}><Undo2 /> {t("backoffice:workspace.undo.action")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={referring} onOpenChange={setReferring}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{t("backoffice:workspace.refer.title")}</DialogTitle>
            <DialogDescription>{t("backoffice:workspace.refer.body")}</DialogDescription>
          </DialogHeader>
          <Field label={t("backoffice:workspace.refer.target")}>
            {(id) => (
              <NativeSelect id={id} value={target} onChange={(e) => setTarget(e.target.value)}>
                {(targets.data ?? []).map((x) => <option key={x.type} value={x.type}>{t(`backoffice:workspace.refer.sections.${x.section}`, { defaultValue: x.section })} · {t(`caseTypes.${x.type}`, { defaultValue: x.type })}</option>)}
              </NativeSelect>
            )}
          </Field>
          <Field label={t("backoffice:workspace.refer.reason")}>{(id) => <Textarea id={id} rows={4} value={reason} onChange={(e) => setReason(e.target.value)} draft={`case:${caseId}:referral:new:reason`} />}</Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReferring(false)}>{t("actions.cancel")}</Button>
            <Button variant="gold" loading={refer.isPending} disabled={!target || reason.trim().length < 10} onClick={() => refer.mutate()}><GitBranch /> {t("backoffice:workspace.refer.submit")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** The cases linked to this one — the file it was referred from and the files referred out of it — as one chip in the header. */
export function LinkedCases({ caseId, parentCaseId }: { caseId: string; parentCaseId: string | null }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const navigate = useNavigate();
  const links = useQuery({ queryKey: ["case-links", caseId], queryFn: () => api.get<LinkedCase[]>(`/api/cases/${caseId}/links`) });
  const items = links.data ?? [];
  if (items.length === 0 && !parentCaseId) return null;
  return (
    <Popover>
      <Tooltip content={t("backoffice:workspace.linked.hint")}>
        <PopoverTrigger asChild>
          <Button size="sm" variant="ghost" className="h-7 px-2"><Link2 /> {t("backoffice:workspace.linked.title")} <Badge size="sm" variant="outline">{items.length}</Badge></Button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align="start" className="w-96 p-2">
        <ul className="space-y-1">
          {items.map((l) => (
            <li key={l.id}>
              <button type="button" onClick={() => navigate(`/cases/${l.id}`)} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-start hover:bg-sand-50">
                <Badge size="sm" variant={l.relation === "Parent" ? "gold" : "outline"}>{t(`backoffice:workspace.linked.relation.${l.relation}`)}</Badge>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{locale === "ar" && l.titleAr ? l.titleAr : l.title}</span>
                  <span className="block text-[11px] text-muted-foreground"><span className="font-mono">{l.caseNumber}</span> · {t(`caseTypes.${l.type}`, { defaultValue: l.type })}</span>
                </span>
                <StatusBadge status={l.status} label={t(`status.${l.status}`, { defaultValue: l.status })} />
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
